import React, { HTMLAttributes, MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

export interface DialogProps extends HTMLAttributes<HTMLDialogElement> {
  open: boolean;
  onClose: () => void;

  closeOnOutsideClick?: boolean;
  children?: React.ReactNode;
  contentClassName?: string;
}

function Dialog({
  open,
  onClose,
  closeOnOutsideClick = true,
  className,
  contentClassName,
  children,
  ...rest
}: DialogProps) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const lastActiveElement = React.useRef<Element | null>(null);

  React.useEffect(() => {
    const dialogNode = dialogRef.current;
    if (open) {
      lastActiveElement.current = document.activeElement;
      dialogNode && dialogNode.showModal();

      document.body.classList.add('overflow-hidden');
    } else {
      dialogNode && dialogNode.close();
      // @ts-ignore
      lastActiveElement.current && lastActiveElement.current.focus();

      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      dialogNode && dialogNode.close();
      // @ts-ignore
      lastActiveElement.current && lastActiveElement.current.focus();

      document.body.classList.remove('overflow-hidden');
    };
  }, [open]);

  React.useEffect(() => {
    const dialogNode = dialogRef.current;
    const handleCancel = (event: { preventDefault: () => void }) => {
      event.preventDefault();
      onClose();
    };
    dialogNode && dialogNode.addEventListener('cancel', handleCancel);
    return () => {
      dialogNode && dialogNode.removeEventListener('cancel', handleCancel);
    };
  }, [onClose, dialogRef]);

  const handleOutsideClick: MouseEventHandler<HTMLDialogElement> = (event) => {
    const dialogNode = dialogRef.current;
    if (closeOnOutsideClick && event.target === dialogNode) {
      onClose();
    }
  };

  return (
    <dialog {...rest} ref={dialogRef} className={twMerge('p-4', className)} onClick={handleOutsideClick}>
      <div className={twMerge('', contentClassName)}>{children}</div>
    </dialog>
  );
}

export default Dialog;
